import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Mail, Send, Loader2, CheckCircle, MapPin, Phone } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [professions, setProfessions] = useState([]);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    profession_interest: '',
    affiliation: '',
    region: '',
    how_found_us: '',
    message: '',
    accepts_marketing: false
  });

  useEffect(() => {
    const fetchProfessions = async () => {
      try {
        const response = await axios.get(`${API}/professions`);
        setProfessions(response.data || []);
      } catch (error) {
        console.error('Error fetching professions:', error);
      }
    };
    fetchProfessions();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.email) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact/lead`, form);
      setSubmitted(true);
      toast.success('¡Mensaje enviado! Nos pondremos en contacto contigo pronto.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar el mensaje');
    } finally {
      setSubmitting(false);
    }
  };

  const regions = [
    'Andalucía', 'Aragón', 'Asturias', 'Baleares', 'Canarias', 'Cantabria',
    'Castilla-La Mancha', 'Castilla y León', 'Cataluña', 'Comunidad Valenciana',
    'Extremadura', 'Galicia', 'La Rioja', 'Madrid', 'Murcia', 'Navarra',
    'País Vasco', 'Ceuta', 'Melilla', 'Estatal'
  ];

  const affiliations = [
    'Estudiante', 'Trabajador en activo', 'Desempleado', 'Funcionario',
    'Personal laboral', 'Personal estatutario', 'Otro'
  ];

  const howFoundOptions = [
    'Google', 'Redes sociales', 'Recomendación de un amigo',
    'Academia/Centro de formación', 'Publicidad', 'Otro'
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">¡Gracias por contactarnos!</h2>
          <p className="text-slate-600 mb-6">
            Hemos recibido tu mensaje. Nos pondremos en contacto contigo lo antes posible.
          </p>
          <Button onClick={() => navigate('/')} className="bg-indigo-600 hover:bg-indigo-700 text-white">
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="border-b border-slate-200 bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">Contacta con nosotros</h1>
            <p className="text-lg text-slate-600 mb-8">
              ¿Tienes alguna pregunta sobre nuestras oposiciones? ¿Necesitas más información?
              Estamos aquí para ayudarte en tu camino hacia tu plaza.
            </p>

            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Email</h3>
                  <p className="text-slate-600">info@tuplazaia.com</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Teléfono</h3>
                  <p className="text-slate-600">+34 900 000 000</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Ubicación</h3>
                  <p className="text-slate-600">España</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-6 text-white">
              <h3 className="text-xl font-bold mb-2">¿Eres una institución?</h3>
              <p className="text-indigo-100 mb-4">
                Ofrecemos planes especiales para academias, hospitales y administraciones públicas.
              </p>
              <Button 
                variant="secondary"
                onClick={() => navigate('/pricing')}
                className="bg-white text-indigo-600 hover:bg-indigo-50"
              >
                Ver Plan Institucional
              </Button>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Envíanos un mensaje</h2>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nombre *</label>
                  <Input
                    required
                    value={form.name}
                    onChange={(e) => setForm({...form, name: e.target.value})}
                    placeholder="Tu nombre completo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                  <Input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({...form, email: e.target.value})}
                    placeholder="tu@email.com"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono</label>
                  <Input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({...form, phone: e.target.value})}
                    placeholder="+34 600 000 000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Comunidad Autónoma</label>
                  <select
                    value={form.region}
                    onChange={(e) => setForm({...form, region: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                  >
                    <option value="">Selecciona...</option>
                    {regions.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Oposición de interés</label>
                  <select
                    value={form.profession_interest}
                    onChange={(e) => setForm({...form, profession_interest: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                  >
                    <option value="">Selecciona...</option>
                    {professions.map(p => (
                      <option key={p.profession_id} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Situación actual</label>
                  <select
                    value={form.affiliation}
                    onChange={(e) => setForm({...form, affiliation: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                  >
                    <option value="">Selecciona...</option>
                    {affiliations.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">¿Cómo nos conociste?</label>
                <select
                  value={form.how_found_us}
                  onChange={(e) => setForm({...form, how_found_us: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                >
                  <option value="">Selecciona...</option>
                  {howFoundOptions.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mensaje</label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({...form, message: e.target.value})}
                  rows={4}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 resize-none"
                  placeholder="Cuéntanos en qué podemos ayudarte..."
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.accepts_marketing}
                  onChange={(e) => setForm({...form, accepts_marketing: e.target.checked})}
                  className="w-4 h-4 mt-1 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-slate-600">
                  Acepto recibir información sobre promociones, novedades y consejos para mi preparación.
                </span>
              </label>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-6 text-lg font-semibold rounded-xl"
              >
                {submitting ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Enviando...</>
                ) : (
                  <><Send className="w-5 h-5 mr-2" /> Enviar mensaje</>
                )}
              </Button>

              <p className="text-xs text-slate-500 text-center">
                Al enviar este formulario aceptas nuestra{' '}
                <a href="/privacidad" className="text-indigo-600 hover:underline">Política de Privacidad</a>.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
